import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { IPermiso, IPopulate, IQueryParam } from 'modelos/src';
import { IBaneoVecino, IConfigVecino } from 'modelosSeguridad/src';
import {
  IColumnas,
  IExportarBoton,
} from '../../../../auxiliares/tabla/tabla.component';
import { IntegracionesService } from '../../../../auxiliares/servicios/http/integraciones.service';
import { DescargasService } from '../../../navigation/descargas/service';
import { VecinosSeguridadExportService } from '../vecinos-seguridad-export.service';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { LoginService } from '../../../login/login.service';
import { escaparHtml } from '../../../../auxiliares/html-seguro.util';
import {
  estadoHtml,
  fechaCelda,
  haceCelda,
} from '../../../../auxiliares/tabla/celdas';

@Component({
  selector: 'app-vecinos-seguridad',
  templateUrl: './vecinos-seguridad.component.html',
  styleUrl: './vecinos-seguridad.component.scss',
  standalone: false,
})
export class VecinosSeguridadComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private integraciones = inject(IntegracionesService);
  private descargas = inject(DescargasService);
  private exportService = inject(VecinosSeguridadExportService);

  public name = 'VecinosSeguridadComponent';
  public permisoActual: IPermiso | undefined = LoginService.getPermisoActual();
  public tieneCredenciales = !!this.permisoActual?.tieneCredencialesSeguridad;

  public datos?: IConfigVecino[];
  public totalCount = 0;
  private baneosPorVecino = new Map<string, IBaneoVecino[]>();
  private llamadaListar = 0;
  public columnas?: IColumnas<IConfigVecino>[];
  public loading = true;

  private vecinos$?: Subscription;

  private populate: IPopulate[] = [
    {
      path: 'cliente',
      select: 'idCategoriaDefault categoriaDefault',
      populate: { path: 'categoriaDefault', select: 'nombre' },
    },
    { path: 'categoria.categoria', select: 'nombre' },
    { path: 'categoria.usuario', select: 'usuario' },
    { path: 'direccion.localidad', select: 'nombre' },
    { path: 'direccion.barrio', select: 'nombre' },
  ];

  public search: IRegExpSearch = {
    fields: [
      'datosPersonales.nombre',
      'datosPersonales.dni',
      'datosPersonales.email',
      'datosPersonales.telefono',
    ],
  };

  public query: IQueryParam = {
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    sort: '-fechaCreacion',
    populate: JSON.stringify(this.populate),
  };

  public filtros: IFiltroTabla[] = this.crearFiltros();

  public botonExportar: IExportarBoton = {
    mostrar: this.helper.tienePermiso('Alertas Seguridad - Exportar'),
    tooltip: 'Exportar los vecinos a planilla',
    color: 'primary',
    tipo: 'material',
    icon: 'table_view',
    accion: () => this.exportar(),
  };

  private crearFiltros(): IFiltroTabla[] {
    const activoFiltro: IFiltroTabla = {
      elementos: [
        { label: 'Activo', value: true },
        { label: 'Inactivo', value: false },
      ],
      filter: { field: 'activo' },
      label: 'Estado',
      rapido: true,
      selectLabel: 'label',
      selectValue: 'value',
      tipo: 'select',
    };

    const fechaFiltro: IFiltroTabla = {
      label: 'Fecha de alta',
      filter: { field: 'fechaCreacion' },
      tipo: 'dateRange',
    };

    const flagsFiltro: IFiltroTabla = {
      elementos: [
        { label: 'Creado por admin', value: 'creadoPorAdmin' },
        { label: 'Importado', value: 'importado' },
        { label: 'DNI escaneado', value: 'dniEscaneado' },
      ],
      filter: { field: 'flagBool' },
      label: 'Origen',
      selectLabel: 'label',
      selectValue: 'value',
      tipo: 'select',
    };

    const baneoFiltro: IFiltroTabla = {
      elementos: [
        { label: 'Baneado', value: 'baneado' },
        { label: 'No baneado', value: 'noBaneado' },
      ],
      filter: { field: 'baneo' },
      label: 'Baneo',
      rapido: true,
      selectLabel: 'label',
      selectValue: 'value',
      tipo: 'select',
    };

    return [activoFiltro, fechaFiltro, flagsFiltro, baneoFiltro];
  }

  private normalizarFiltro(filterFromQuery: any): any {
    const filter = { ...filterFromQuery } as Record<string, any>;
    let $and: any[] = Array.isArray(filter['$and']) ? [...filter['$and']] : [];

    $and = $and.flatMap((c) => {
      const cc = c as Record<string, any>;
      if ('flagBool' in cc && typeof cc['flagBool'] === 'string') {
        return [{ [cc['flagBool']]: true }];
      }
      return [c];
    });

    const tieneActivo =
      Object.prototype.hasOwnProperty.call(filter, 'activo') ||
      $and.some((c) => Object.prototype.hasOwnProperty.call(c, 'activo'));
    if (!tieneActivo) $and.push({ activo: true });

    return { ...filter, ...($and.length ? { $and } : {}) };
  }

  private async idsVecinosBaneados(): Promise<string[]> {
    const query: IQueryParam = {
      filter: JSON.stringify({ hasta: { $gte: new Date().toISOString() } }),
      select: 'idConfigVecino idVecino',
      limit: 0,
      unlimited: true,
    };
    const res = await this.integraciones.getBaneosVecinosFiltered(query);
    const ids = new Set<string>();
    for (const baneo of res?.datos || []) {
      const id = baneo.idConfigVecino || baneo.idVecino;
      if (id) ids.add(id);
    }
    return [...ids];
  }

  /**
   * El baneo vive en otra colección, no se puede filtrar directo en
   * configvecinos: se resuelven los ids baneados y se filtra por _id.
   */
  private async aplicarFiltroBaneo(filtro: any): Promise<any> {
    const $and: any[] = Array.isArray(filtro?.$and) ? filtro.$and : [];
    const enAnd = $and.find((c) => 'baneo' in c);
    const valor = enAnd ? enAnd['baneo'] : filtro?.baneo;
    if (valor !== 'baneado' && valor !== 'noBaneado') return filtro;

    const nuevo = { ...filtro };
    delete nuevo.baneo;
    const nuevoAnd = $and.filter((c) => !('baneo' in c));

    try {
      const ids = await this.idsVecinosBaneados();
      nuevoAnd.push({
        _id: valor === 'baneado' ? { $in: ids } : { $nin: ids },
      });
    } catch (error) {
      console.error('Error resolviendo filtro de baneo', error);
      this.helper.notifWarn(
        'No se pudo consultar los baneos: el filtro de baneo no se aplicó',
      );
    }

    if (nuevoAnd.length) nuevo.$and = nuevoAnd;
    else delete nuevo.$and;
    return nuevo;
  }

  private exportar(): void {
    if (!this.tieneCredenciales) return;

    const filterFromQuery = this.query.filter
      ? JSON.parse(this.query.filter)
      : {};
    const filtroFinal = this.normalizarFiltro(filterFromQuery);
    const queryFinal: IQueryParam = {
      ...this.query,
      page: 0,
      limit: 0,
      unlimited: true,
      filter: JSON.stringify(filtroFinal),
    };

    const fecha = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const nombre = `vecinos_seguridad_${fecha}.xlsx`;

    this.descargas.agregarDescargaExcel(nombre, async () => {
      const filtroExport = await this.aplicarFiltroBaneo(filtroFinal);
      const res = await this.integraciones.getConfigVecinosFiltered({
        ...queryFinal,
        filter: JSON.stringify(filtroExport),
      });
      const datos = res?.datos || [];
      return this.exportService.construirBlob(datos);
    });

    this.helper.notifSuccess(
      'Exportación iniciada: revisá el panel de Descargas',
    );
  }

  public async listar(): Promise<void> {
    if (!this.tieneCredenciales) {
      this.datos = [];
      this.totalCount = 0;
      return;
    }

    const llamada = ++this.llamadaListar;
    const filterFromQuery = this.query.filter
      ? JSON.parse(this.query.filter)
      : {};
    const filtroFinal = await this.aplicarFiltroBaneo(
      this.normalizarFiltro(filterFromQuery),
    );
    // Otra llamada más nueva ya está en curso
    if (llamada !== this.llamadaListar) return;
    const queryFinal: IQueryParam = {
      ...this.query,
      filter: JSON.stringify(filtroFinal),
    };

    this.vecinos$?.unsubscribe();
    this.vecinos$ = this.lista2
      .configVecinos(queryFinal, 60)
      .subscribe(async (res) => {
        const datos = res?.datos || [];
        await this.cargarBaneos(datos);
        this.totalCount = res?.totalCount || 0;
        this.datos = datos;
      });
  }

  private async cargarBaneos(vecinos: IConfigVecino[]): Promise<void> {
    const ids = vecinos.map((v) => v._id).filter(Boolean) as string[];
    const baneos = new Map<string, IBaneoVecino[]>();
    if (ids.length) {
      const query: IQueryParam = {
        filter: JSON.stringify({
          $or: [{ idConfigVecino: { $in: ids } }, { idVecino: { $in: ids } }],
          hasta: { $gte: new Date().toISOString() },
        }),
        limit: 0,
        unlimited: true,
      };
      try {
        const res = await this.integraciones.getBaneosVecinosFiltered(query);
        for (const baneo of res?.datos || []) {
          const id = baneo.idConfigVecino || baneo.idVecino;
          if (!id) continue;
          const arr = baneos.get(id) || [];
          arr.push(baneo);
          baneos.set(id, arr);
        }
      } catch (error) {
        console.error('Error cargando baneos de vecinos', error);
      }
    }
    this.baneosPorVecino = baneos;
  }

  /** Origen del vecino (antes íconos sueltos con title). */
  private origen(row: IConfigVecino): string[] {
    const partes: string[] = [];
    if (row.creadoPorAdmin) partes.push('Creado por admin');
    if (row.importado) partes.push('Importado');
    if (row.dniEscaneado) partes.push('DNI escaneado');
    return partes;
  }

  private tipoBaneo(b: IBaneoVecino): string {
    return b.tipo ? b.tipo.charAt(0).toUpperCase() + b.tipo.slice(1) : 'Baneo';
  }

  // Los datos del vecino los carga él mismo desde la app móvil y app-tabla
  // pinta row.html sin sanitizar: todo dato interpolado va escapado (XSS
  // almacenado).
  private setColumnas(): void {
    this.columnas = [
      {
        header: { label: 'Vecino' },
        row: {
          html: (row) => {
            const nombre = row.datosPersonales?.nombre;
            const dni = row.datosPersonales?.dni;
            if (!nombre && !dni) return '';
            let html = `<div class="tabla-celda-col">`;
            if (nombre) {
              html += `<div class="tabla-linea">
                <span class="material-symbols-outlined">person</span>
                <span class="tabla-principal">${escaparHtml(nombre)}</span>
              </div>`;
            }
            if (dni) {
              html += `<span class="tabla-secundario">DNI ${escaparHtml(dni)}</span>`;
            }
            html += `</div>`;
            return html;
          },
          noData: 'Sin datos',
        },
      },
      {
        header: { label: 'Contacto' },
        row: {
          html: (row) => {
            const email = row.datosPersonales?.email;
            const tel = row.datosPersonales?.telefono;
            if (!email && !tel) return '';
            let html = `<div class="tabla-celda-col">`;
            if (email) {
              html += `<span>${escaparHtml(email)}</span>`;
            }
            if (tel) {
              html += `<span class="tabla-secundario">Tel. ${escaparHtml(tel)}</span>`;
            }
            html += `</div>`;
            return html;
          },
          noData: 'Sin contacto',
        },
      },
      {
        header: { label: 'Dirección' },
        row: {
          html: (row) => {
            const dir = row.direccion?.direccion;
            if (!dir) return '';
            return `<div class="tabla-linea">
              <span class="material-symbols-outlined">place</span>
              <span>${escaparHtml(HelperService.extraerDireccion(dir))}</span>
            </div>`;
          },
          tooltip: (row) => row.direccion?.direccion || undefined,
          noData: 'Sin dirección',
        },
      },
      {
        header: { label: 'Localidad / barrio' },
        row: {
          html: (row) => {
            const loc = row.direccion?.localidad?.nombre;
            const barrio = row.direccion?.barrio?.nombre;
            if (!loc && !barrio) return '';
            let html = `<div class="tabla-celda-col">`;
            html += loc
              ? `<span>${escaparHtml(loc)}</span>`
              : `<span class="no-info">Sin localidad</span>`;
            if (barrio) {
              html += `<span class="tabla-secundario">Barrio ${escaparHtml(barrio)}</span>`;
            }
            html += `</div>`;
            return html;
          },
          noData: 'Sin ubicación',
        },
      },
      {
        header: { label: 'Categoría' },
        row: {
          html: (row) => {
            const cat = row.categoria?.categoria?.nombre;
            const def = row.cliente?.categoriaDefault?.nombre;
            const display = cat || def;
            if (!display) return '';
            let html = `<div class="tabla-celda-col"><span>${escaparHtml(display)}</span>`;
            if (!cat) {
              html += `<span class="tabla-secundario">Por defecto</span>`;
            }
            html += `</div>`;
            return html;
          },
          // Quién la asignó (antes en el title)
          tooltip: (row) => {
            const usuario = row.categoria?.usuario?.usuario;
            return usuario ? `Por: ${escaparHtml(usuario)}` : undefined;
          },
          noData: 'Sin categoría',
        },
      },
      {
        header: { label: 'Estado' },
        row: {
          estado: (row) => {
            const origen = this.origen(row);
            return {
              texto: row.activo ? 'Activo' : 'Inactivo',
              tono: row.activo ? 'ok' : 'neutro',
              icono: row.activo ? 'toggle_on' : 'toggle_off',
              detalle: origen.length ? origen.join(' · ') : undefined,
            };
          },
        },
      },
      {
        header: { label: 'Baneo' },
        row: {
          html: (row) => {
            const baneos = row._id
              ? this.baneosPorVecino.get(row._id)
              : undefined;
            if (!baneos?.length) {
              return estadoHtml({
                texto: 'Sin baneos',
                tono: 'ok',
                icono: 'check_circle',
              });
            }
            const partes = baneos.map((b) => {
              const texto = b.hasta
                ? `Baneado hasta ${fechaCelda(b.hasta).slice(0, 10)}`
                : 'Baneado sin fecha de fin';
              return `<span>${estadoHtml({ texto, tono: 'error', icono: 'block' })}</span>`;
            });
            return `<div class="tabla-celda-col">${partes.join('')}</div>`;
          },
          // Tipo y período de cada baneo (antes en el title)
          tooltip: (row) => {
            const baneos = row._id
              ? this.baneosPorVecino.get(row._id)
              : undefined;
            if (!baneos?.length) return undefined;
            return baneos
              .map((b) => {
                const desde = b.desde ? ` desde ${fechaCelda(b.desde)}` : '';
                const hasta = b.hasta
                  ? ` hasta ${fechaCelda(b.hasta)}`
                  : ' sin fecha de fin';
                return `<div><strong>${escaparHtml(this.tipoBaneo(b))}</strong>${desde}${hasta}</div>`;
              })
              .join('');
          },
        },
      },
      {
        header: { label: 'Fecha de alta', sortable: true, sort: 'fechaCreacion' },
        row: {
          fecha: (row) => row.fechaCreacion,
          noData: 'Sin fecha',
        },
      },
      {
        header: {
          label: 'Último acceso',
          sortable: true,
          sort: 'ultimoAcceso',
        },
        row: {
          html: (row) => {
            if (!row.ultimoAcceso) return '';
            let html = `<div class="tabla-celda-col tabla-fecha">`;
            html += `<span>${fechaCelda(row.ultimoAcceso)}</span>`;
            html += `<span class="tabla-secundario">${haceCelda(row.ultimoAcceso)}</span>`;
            const app = [row.app, row.appVersion].filter(Boolean).join(' ');
            if (app) {
              html += `<span class="tabla-secundario">${escaparHtml(app)}</span>`;
            }
            html += `</div>`;
            return html;
          },
          noData: 'Sin acceso',
        },
      },
    ];
  }

  ngOnInit(): void {
    this.setColumnas();
    this.listar();
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.vecinos$?.unsubscribe();
  }
}
